import React, { FormEvent, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import InputGroup from 'react-bootstrap/InputGroup';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link, useNavigate } from '@tanstack/react-location';
import { useAuth } from '../hooks/useAuth';
import { loginRequest } from '../requests/session.request';

// Brand colors matching wefighttickets.com
const colors = {
  orange: '#fc6f23',
  orangeHover: '#d8531a',
  darkInk: '#191b1f',
  mutedGray: '#5c606b',
  border: '#ece8e2',
  cream: '#faf8f5',
  white: '#ffffff'
};

const styles = {
  pageBackground: {
    minHeight: '100vh',
    backgroundColor: colors.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  } as React.CSSProperties,
  card: {
    maxWidth: 440,
    width: '100%',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: 20,
    boxShadow: '0 30px 60px -42px rgba(0,0,0,0.15)',
    padding: 30
  } as React.CSSProperties,
  logo: {
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.darkInk,
    margin: 0
  } as React.CSSProperties,
  logoAccent: {
    color: colors.orange
  } as React.CSSProperties,
  heading: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.darkInk,
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  label: {
    fontSize: '.76rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.mutedGray,
    marginBottom: 8
  } as React.CSSProperties,
  input: {
    padding: '13px 14px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    backgroundColor: colors.white
  } as React.CSSProperties,
  inputGroup: {
    borderRadius: 10,
    overflow: 'hidden'
  } as React.CSSProperties,
  showPasswordBtn: {
    border: `1px solid ${colors.border}`,
    borderLeft: 'none',
    backgroundColor: colors.white,
    color: colors.mutedGray
  } as React.CSSProperties,
  submitBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    fontWeight: 800,
    fontSize: 16,
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    marginTop: 8
  } as React.CSSProperties,
  forgotLink: {
    fontSize: 14,
    color: colors.orange,
    textDecoration: 'none',
    padding: 0,
    marginTop: 8,
    display: 'inline-block'
  } as React.CSSProperties
};

// In-house sign-in (replaces the Auth0 redirect). The file keeps its old name so routes and
// imports stay stable; rename to LoginPage.tsx in a follow-up.
function LoginPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: user?.mustChangePassword ? '/change-password' : '/home', replace: true });
    }
  }, [isAuthenticated, user?.mustChangePassword, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await loginRequest(email.trim(), password);
      // The effect above navigates once the session is stored.
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.pageBackground}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <h1 style={styles.logoText}>
            WE FIGHT <span style={styles.logoAccent}>TICKETS</span>
          </h1>
        </div>
        <h2 style={styles.heading}>Sign in to your account</h2>
        <Form onSubmit={onSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3" controlId="loginEmail">
            <Form.Label style={styles.label}>Email</Form.Label>
            <Form.Control
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              style={styles.input}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="loginPassword">
            <Form.Label style={styles.label}>Password</Form.Label>
            <InputGroup style={styles.inputGroup}>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ ...styles.input, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                style={styles.showPasswordBtn}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            </InputGroup>
          </Form.Group>
          <Button type="submit" disabled={submitting} style={styles.submitBtn}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Sign In'}
          </Button>
        </Form>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/forgot-password" style={styles.forgotLink}>
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
